import { useContext, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import mixpanel from "mixpanel-browser";

// eslint-disable-next-line import/default, import/no-named-as-default-member
import authenticate from "../../utils/authenticate";

import FullPageLoader from "../../components/full-page-loader";
import { AppContext } from "../../context/AppContext";
import TransferScreen from "../../widgets/transfer-screen";
import sendError from "../../utils/sendError";
import { BRIDGE_PRODUCTION, ERROR_CODE_400 } from "../../typings";
import sendOpen from "../../utils/sendOpen";
import parseUrl from "../../utils/parseUrl";
import TransferDetails from "../../class/transferDetails";
import { TransferContext } from "../../context/TransferContext";
import UserDetails from "../../class/userDetails";
import { sampleTransferIntentInResponseIra } from "../../api/mockAPIResponse";
import { sendAppVersionToSentry, setSentryContext } from "../../lib/sentry";

import ErrorPane from "../../widgets/general/error-pane";
import Analytics from "../../utils/productAnalytics";
import { create } from "axios";
import { TransferIntent } from "../../class/transferIntent";

const Home = () => {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const {
    setAppEnv,
    setPartnerConfig,
    isAppLoading,
    setIsAppLoading,
    setTransferIntentId,
    setAccessToken,
    setTransferId,
    setClientId,
    setIsWebview,
    setParentUrl,
    setFlow,
  } = useContext(AppContext);
  const {
    updateTransferDetails,
    setTransferIntentDetails,
    updateUserDetails,
    setTransferInstructions,
    setUserId,
    setCanStart,
    updateAPIInstance,
  } = useContext(TransferContext);

  useEffect(() => {
    const init = async () => {
      const { accessToken, clientId, transferIntentId, userId, environment, webview, origin } = parseUrl(
        window.location.href,
      );
      sendOpen("init", origin);
      setIsAppLoading(true);
      const parentUrl = document.location.ancestorOrigins
        ? document.location.ancestorOrigins[0]
        : document.referrer
          ? document.referrer
          : undefined;
      const nodeEnv = process.env.NODE_ENV || "development";

      if (nodeEnv !== "test" && !parentUrl && !webview) return;
      setParentUrl(origin);
      sendOpen("loading", origin);
      const {
        ready,
        env = undefined,
        errorCode = undefined,
        errorMessage = undefined,
        clientName = undefined,
        data = {},
      } = await authenticate({
        accessToken,
        clientId,
        transferIntentId,
        userId,
        environment,
      });
      if (errorCode && errorMessage) {
        sendError({ errorCode, errorMessage }, webview, origin);
        sendOpen("error", origin);
        return;
      }

      setIsWebview(webview);

      setAppEnv(env);
      // default mixpanelId for dev
      let mixpanelId = "bd5d25f14e000177792ba847934b1685";
      if (process.env.REACT_APP_ENV === "production" && environment !== "development") {
        // this is the production project id
        mixpanelId = "4923764bd4dfd40af65dc365fd82f44b";
      }
      mixpanel.init(mixpanelId, {
        debug: false,
        track_pageview: false,
        persistence: "localStorage",
        record_sessions_percent: 100,
        record_heatmap_data: true,
        autocapture: {
          click: true,
          capture_text_content: true,
          capture_extra_attrs: ["data-button-title", "data-name", "data-current-url", "name"],
        },
        record_mask_text_selector: "",
      });

      if (env === "BRIDGE_DEMO" || env === "BRIDGE_DEVELOPMENT") {
        mixpanel.opt_out_tracking();
      }

      if (clientName) {
        setPartnerConfig(clientName);
        Analytics.setPartnerName(clientName);
      }

      if (ready) {
        updateAPIInstance({ accessToken, clientId, env: env || "development" });
        const {
          transferIntentDetails = undefined,
          transferInstructionDetails = [], // TODO: STOP HERE: FETCH USER DETAILS DIRECTLY if transfer instruction details are not empty
        } = data;
        if (transferIntentDetails) {
          setTransferIntentDetails(new TransferIntent(transferIntentDetails));
          Analytics.setUserProfiles({
            userName: `${transferIntentDetails.userIdentity?.userDetails?.firstName || ""} ${transferIntentDetails.userIdentity.userDetails?.lastName || ""}`,
            userEmail: transferIntentDetails.userIdentity?.email,
            partnerName: clientName || "",
            partnerId: clientId,
          });
        }
        if (transferInstructionDetails.length > 0) {
          setTransferInstructions(transferInstructionDetails);
        }
        if (!transferIntentDetails && transferInstructionDetails.length === 0) {
          sendError(
            { errorCode: ERROR_CODE_400, errorMessage: "Transfer Intent Id or UserId not found" },
            webview,
            origin,
          );
          sendOpen("error", origin);
          return;
        }
        if (clientId.includes("bridge")) {
          mixpanel.opt_out_tracking();
          updateUserDetails(UserDetails.fromTransferIntent(sampleTransferIntentInResponseIra.userIdentity.userDetails));
          setCanStart(true);
        } else {
          setSentryContext({ transferIntentId, userId, clientId });
          sendAppVersionToSentry();
          let updatedTransferIntentId = "";
          if (transferIntentDetails) {
            updatedTransferIntentId = transferIntentDetails.transferIntentReference;
          }
          if (!updatedTransferIntentId && transferIntentId && transferIntentId !== "undefined") {
            updatedTransferIntentId = transferIntentId;
          }
          setTransferIntentId(updatedTransferIntentId);
          const apiInstance = create({
            baseURL:
              env === BRIDGE_PRODUCTION ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_API_BASE_URL,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              ClientId: clientId,
              "X-Auth-Dispatch": "EXTERNAL",
            },
          });
          if (transferIntentDetails?.status === "INPROGRESS" || transferIntentDetails?.status === "COMPLETE") {
            // fetch transfer details
            let $userId;
            let transferDetails;
            try {
              const transferDetailsRes = await apiInstance({
                method: "GET",
                url: `/api/userIdentity/transferIntent/${transferIntentDetails.transferIntentReference}/transfer`,
              });
              // update transfer details
              transferDetails = transferDetailsRes.data.data;
              updateTransferDetails(TransferDetails.fromAPI(transferDetails));
              setTransferId(transferDetails.transferId);
            } catch (e) {
              // do nothing
            }
            // fetch user details
            try {
              $userId = transferDetails.userId;

              if ($userId) {
                setUserId($userId);
                const userDetailsRes = await apiInstance({
                  method: "GET",
                  url: `/api/userIdentity/${$userId}/userDetails`,
                });
                const userResponseData = userDetailsRes.data.data.userDetails;
                updateUserDetails(
                  new UserDetails({
                    ...userResponseData,
                  }),
                );
              }
            } catch (e) {
              // do nothing
            }
            // Fetch transferInstructions
            if (transferIntentDetails?.status === "COMPLETE") {
              const transferInstructionRes = await apiInstance({
                method: "GET",
                url: `/api/userIdentity/${$userId}/transfer/transferInstruction`,
              });
              setTransferInstructions(transferInstructionRes.data.data);
            }
          } else if (transferIntentDetails) {
            updateUserDetails(UserDetails.fromTransferIntent(transferIntentDetails?.userIdentity.userDetails));
          }
        }
        if (transferIntentDetails) {
          const isAnnuity = new TransferIntent(transferIntentDetails).isAnnuityTransfer;
          setFlow(isAnnuity ? "Annuity" : "Rollover");
          if (isAnnuity && transferIntentDetails?.subClient?.name) {
            setPartnerConfig(transferIntentDetails?.subClient.name);
          }
        }
        setAccessToken(accessToken);
        setClientId(clientId);
        setCanStart(true);
        setIsAppLoading(false);
        setIsAuth(ready);
        sendOpen("success", origin);
      }
    };
    init();
  }, []);

  if (isAppLoading) return <FullPageLoader />;
  return isAuth ? (
    <ErrorBoundary FallbackComponent={ErrorPane}>
      <TransferScreen />
    </ErrorBoundary>
  ) : (
    <FullPageLoader />
  );
};

export default Home;
